import React from 'react';
import css from '@styled-system/css';
import styled from 'styled-components';
import { compose, display, space, variant } from 'styled-system';

import {
  A11yHidden,
  fontWeight,
  textAlign,
  textColor,
  textDecoration,
  ThemeHeadingSizes,
} from '@rover/kibble/styles';
import roverTheme from '@rover/kibble/themes/roverTheme';

import Box from '../Box';

import { HeadingCommonProps, HeadingTags } from './Heading.common';

export type Props = HeadingCommonProps;

export const DEFAULT_TAGS_FOR_SIZES: Record<ThemeHeadingSizes, HeadingTags> = {
  '50': 'h6',
  '100': 'h5',
  '200': 'h4',
  '300': 'h3',
  '400': 'h2',
  '500': 'h1',
  '600': 'div',
};

export const getTag = (size: ThemeHeadingSizes, as?: HeadingTags): HeadingTags => {
  if (as) {
    return as;
  }

  return DEFAULT_TAGS_FOR_SIZES[size];
};

const StyledHeading = styled(Box)<
  Omit<Props, 'as' | 'webProps' | 'rxnProps'> & {
    $a11yHidden: boolean;
    $responsive: boolean;
  }
>(
  // Resets values in case of global styles
  {
    padding: '0',
    margin: '0',
    border: '0 none',
  },
  (props) => {
    const heading = props.theme?.heading ? props.theme.heading : roverTheme.heading;
    const headingResponsive = props.theme?.headingResponsive
      ? props.theme.headingResponsive
      : roverTheme.headingResponsive;
    return variant({
      prop: 'size',
      variants: {
        50: heading[50],
        100: heading[100],
        200: heading[200],
        300: heading[300],
        400: {
          ...heading[400],
          fontSize: props.$responsive
            ? [heading[400].fontSize, headingResponsive[400].fontSize]
            : heading[400].fontSize,
        },
        500: {
          ...heading[500],
          fontSize: props.$responsive
            ? [heading[500].fontSize, headingResponsive[500].fontSize]
            : heading[500].fontSize,
        },
        600: {
          ...heading[600],
          fontSize: props.$responsive
            ? [heading[600].fontSize, headingResponsive[600].fontSize]
            : heading[600].fontSize,
        },
      },
    })(props);
  },
  (props) => (props.$a11yHidden ? `${A11yHidden}` : ''),
  compose(textAlign, textColor, textDecoration, fontWeight, space, display), // Apply last to avoid being overwritten by resets and variant styles
  // support the "sx" prop.  This comes last so it will override any other styles
  (props) => css(props.sx)(props.theme)
);

function Heading({
  webProps = {},
  size = '200',
  testID,
  // pull out rxnProps and do nothing with them because this is the web impl
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  rxnProps,
  ...other
}: Props): JSX.Element {
  const { a11yHidden = false, responsive = true, as, ...otherWebProps } = webProps;
  return (
    <StyledHeading
      {...other}
      {...otherWebProps}
      as={getTag(size, as)}
      $a11yHidden={a11yHidden}
      $responsive={responsive}
      size={size}
      data-testid={testID}
    />
  );
}

/**
 * Heading is a semantic text component for section headings.
 * Automatically selects appropriate heading tags (h1-h6) based on size.
 *
 * READ THE DOCS: https://roverdotcom.atlassian.net/wiki/spaces/TECH/pages/5212438752/Kibble+Core+Heading
 */
export default Heading;
